import { forwardRef, type ReactNode } from 'react';

import cn from 'classnames';

import {
    layoutSprinkles,
    stackingSprinkles,
    type LayoutSprinkles,
    type StackingSprinkles,
} from '../../styles/sprinkles.css';

type BoxSprinkles = LayoutSprinkles & Omit<StackingSprinkles, 'isolation'>;

export type BoxProps = Partial<BoxSprinkles> &
    React.HTMLAttributes<HTMLDivElement> & {
        children?: ReactNode;
        isolate?: boolean;
    };

export const Box = forwardRef<HTMLDivElement, BoxProps>(
    (
        {
            className,
            children,
            // Sprinkle props
            display,
            flexDirection,
            justifyContent,
            alignItems,
            alignSelf,
            flex,
            flexShrink,
            flexBasis,
            flexGrow,
            flexWrap,
            minWidth,
            minHeight,
            width,
            height,
            overflow,
            inset,
            marginBlock,
            marginBlockStart,
            marginBlockEnd,
            marginInline,
            marginInlineStart,
            marginInlineEnd,
            paddingBlock,
            paddingBlockStart,
            paddingBlockEnd,
            paddingInline,
            paddingInlineStart,
            paddingInlineEnd,
            gap,
            columnGap,
            background,
            borderColor,
            boxShadow,
            borderWidth,
            borderRadius,
            borderBottom,
            // Shorthands
            m,
            my,
            mx,
            mt,
            mb,
            ml,
            mr,
            p,
            py,
            px,
            pt,
            pb,
            pl,
            pr,
            position,
            top,
            bottom,
            right,
            left,
            bg,
            radius,
            shadow,
            isolate,
            zIndex,
            z,
            pointerEvents,
            ...rest
        },
        ref,
    ) => {
        const sprinkleClasses = layoutSprinkles({
            display,
            flexDirection,
            justifyContent,
            alignItems,
            alignSelf,
            flex,
            flexShrink,
            flexBasis,
            flexGrow,
            flexWrap,
            minWidth,
            minHeight,
            width,
            height,
            overflow,
            inset,
            marginBlock,
            marginBlockStart,
            marginBlockEnd,
            marginInline,
            marginInlineStart,
            marginInlineEnd,
            paddingBlock,
            paddingBlockStart,
            paddingBlockEnd,
            paddingInline,
            paddingInlineStart,
            paddingInlineEnd,
            gap,
            columnGap,
            background,
            boxShadow,
            bg,
            borderColor,
            radius,
            borderRadius,
            borderWidth,
            borderBottom,
            shadow,
            m,
            my,
            mx,
            mt,
            mb,
            ml,
            mr,
            p,
            py,
            px,
            pt,
            pb,
            pl,
            pr,
            position,
            top,
            bottom,
            right,
            left,
            pointerEvents,
        });
        const stackingClasses = stackingSprinkles({ isolation: isolate ? 'isolate' : undefined, zIndex, z });
        return (
            <div className={cn(sprinkleClasses, stackingClasses, className)} {...rest} ref={ref}>
                {children}
            </div>
        );
    },
);

Box.displayName = 'Box';
